import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Snowflake,
  Users,
  Ticket,
  Mail,
  Calendar as CalendarIcon,
  Trash2,
  Edit,
  LogOut,
  RefreshCw,
  Lock,
  Unlock,
  Phone,
  Clock,
  AlertCircle,
  QrCode,
  Gift,
  CheckCircle2,
  Ban,
  Plus,
  FileText,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { de } from "date-fns/locale";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Season dates
const SEASON_START = new Date(2026, 10, 27); // Nov 27, 2026

// Helper: Convert any axios error into a safe, human-readable string.
// FastAPI returns 422 with detail = Array<{type, loc, msg, ...}> which React can't render.
const getErrorMessage = (error, fallback = "Ein Fehler ist aufgetreten") => {
  const detail = error?.response?.data?.detail;
  if (!detail) return error?.message || fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return (
      detail
        .map((d) => {
          if (typeof d === "string") return d;
          if (d && typeof d === "object") {
            const field = Array.isArray(d.loc) ? d.loc[d.loc.length - 1] : "";
            return field ? `${field}: ${d.msg || ""}` : (d.msg || "");
          }
          return "";
        })
        .filter(Boolean)
        .join(" · ") || fallback
    );
  }
  return fallback;
};
const SEASON_END = new Date(2027, 0, 6); // Jan 6, 2027

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("anfragen");
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [contacts, setContacts] = useState([]);
  const [silvesterBookings, setSilvesterBookings] = useState([]);
  const [eisstockBookings, setEisstockBookings] = useState([]);
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [gutscheine, setGutscheine] = useState([]);
  
  // Calendar state
  const [selectedDate, setSelectedDate] = useState(SEASON_START);
  const [daySlots, setDaySlots] = useState([]);
  const [openingHours, setOpeningHours] = useState(null);
  const [isInSeason, setIsInSeason] = useState(true);
  
  // Edit dialog state
  const [editDialog, setEditDialog] = useState({ open: false, type: null, data: null });
  const [editForm, setEditForm] = useState({});
  
  // Block slot dialog
  const [blockDialog, setBlockDialog] = useState({ open: false, date: null, slot: null, lane: null });
  const [blockReason, setBlockReason] = useState("");

  // Confirm delete dialog (generic)
  // item: { kind: 'contact'|'silvester'|'eisstock'|'gutschein', id: string, label?: string }
  const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null });

  // Cancel/Refund dialog
  // target: { kind: 'silvester'|'eisstock'|'gutschein', id: string, label: string, hasCapture: boolean }
  const [cancelDialog, setCancelDialog] = useState({ open: false, target: null });
  const [cancelForm, setCancelForm] = useState({ refund: false, notify_customer: true, reason: "" });
  const [cancelBusy, setCancelBusy] = useState(false);

  // Manual booking dialog (admin own bookings without service fee)
  // type: 'silvester' | 'eisstock'
  const [manualDialog, setManualDialog] = useState({ open: false, type: null });
  const [manualForm, setManualForm] = useState({});
  const [manualBusy, setManualBusy] = useState(false);

  // VIP Sitzplatz-Overview
  const [vipSeatsData, setVipSeatsData] = useState(null);
  const [vipCapacityInput, setVipCapacityInput] = useState(250);
  const [vipCapacityBusy, setVipCapacityBusy] = useState(false);

  // PayPal-Link senden Dialog
  // type: 'silvester' | 'eisstock' | 'gutschein'
  const [linkDialog, setLinkDialog] = useState({ open: false, type: null });
  const [linkForm, setLinkForm] = useState({});
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkResult, setLinkResult] = useState(null); // {pay_url, total}

  // Invoice audit log (Rechnungsbuch)
  const [invoiceAudit, setInvoiceAudit] = useState(null); // {records:[], total, gaps:[]}
  const [invoiceAuditLoading, setInvoiceAuditLoading] = useState(false);

  const getAuthHeaders = () => {
    const auth = sessionStorage.getItem("adminAuth");
    if (!auth) {
      navigate("/admin");
      return null;
    }
    return { Authorization: `Basic ${auth}` };
  };

  const downloadInvoice = async (kind, id, label) => {
    const headers = getAuthHeaders();
    if (!headers) return;
    try {
      const r = await fetch(`${API}/admin/invoice/${kind}/${id}`, { headers });
      if (!r.ok) {
        const txt = await r.text().catch(() => "");
        toast.error(txt && txt.length < 200 ? txt : "Rechnung konnte nicht erstellt werden");
        return;
      }
      // Prefer the server-provided filename (contains the official invoice number)
      const cd = r.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/i);
      const filename = (m && m[1]) || `Rechnung-${label || id}.pdf`;
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success("Rechnung heruntergeladen");
    } catch (e) {
      toast.error("Rechnung-Download fehlgeschlagen");
    }
  };

  const downloadCreditNote = async (kind, id) => {
    const headers = getAuthHeaders();
    if (!headers) return;
    try {
      const r = await fetch(`${API}/admin/credit-note/${kind}/${id}`, { headers });
      if (!r.ok) {
        const txt = await r.text().catch(() => "");
        toast.error(txt && txt.length < 200 ? txt : "Stornorechnung nicht verfügbar");
        return;
      }
      const cd = r.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/i);
      const filename = (m && m[1]) || `Stornorechnung-${id}.pdf`;
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success("Stornorechnung heruntergeladen");
    } catch (e) {
      toast.error("Stornorechnung-Download fehlgeschlagen");
    }
  };

  const downloadTicket = async (kind, id) => {
    const headers = getAuthHeaders();
    if (!headers) return;
    try {
      const r = await fetch(`${API}/admin/ticket/${kind}/${id}`, { headers });
      if (!r.ok) {
        const txt = await r.text().catch(() => "");
        toast.error(txt && txt.length < 200 ? txt : "Ticket-PDF nicht verfügbar");
        return;
      }
      const cd = r.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/i);
      const filename = (m && m[1]) || `Ticket-${id}.pdf`;
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success("Ticket heruntergeladen");
    } catch (e) {
      toast.error("Ticket-Download fehlgeschlagen");
    }
  };

  const fetchInvoiceAudit = async () => {
    const headers = getAuthHeaders();
    if (!headers) return;
    setInvoiceAuditLoading(true);
    try {
      const r = await axios.get(`${API}/admin/invoices/audit`, { headers });
      setInvoiceAudit(r.data);
    } catch (e) {
      toast.error("Rechnungsbuch konnte nicht geladen werden");
    } finally {
      setInvoiceAuditLoading(false);
    }
  };

  const fetchData = async () => {
    const headers = getAuthHeaders();
    if (!headers) return;

    setLoading(true);
    try {
      const [contactsRes, silvesterRes, eisstockRes, blockedRes, gutscheineRes, seatsRes] = await Promise.all([
        axios.get(`${API}/admin/contacts`, { headers }),
        axios.get(`${API}/admin/silvester`, { headers }),
        axios.get(`${API}/admin/eisstock`, { headers }),
        axios.get(`${API}/admin/blocked-slots`, { headers }),
        axios.get(`${API}/admin/gutscheine`, { headers }),
        axios.get(`${API}/admin/silvester/seats`, { headers }),
      ]);
      
      setContacts(contactsRes.data);
      setSilvesterBookings(silvesterRes.data);
      setEisstockBookings(eisstockRes.data);
      setBlockedSlots(blockedRes.data);
      setGutscheine(gutscheineRes.data);
      setVipSeatsData(seatsRes.data);
      setVipCapacityInput(seatsRes.data?.capacity_open ?? 250);
    } catch (error) {
      if (error.response?.status === 401) {
        sessionStorage.removeItem("adminAuth");
        navigate("/admin");
      } else {
        toast.error("Fehler beim Laden der Daten");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Fetch slots from API when date changes
  useEffect(() => {
    const fetchDaySlots = async () => {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      try {
        const response = await axios.get(`${API}/eisstock/slots-v2/${dateStr}`);
        const data = response.data;
        
        setIsInSeason(data.in_season);
        setOpeningHours(data.opening_hours);
        
        // Map API slots to include booking info from local data
        const bookingsOnDate = eisstockBookings.filter(b => b.booking_date === dateStr);
        const blockedOnDate = blockedSlots.filter(b => b.date === dateStr);
        
        const slots = (data.slots || []).map(slot => {
          // Per-lane state: { 1: {booking: {...}}, 2: {blocked: {...}}, 3: null }
          const lanes = {};
          for (let n = 1; n <= (slot.total_lanes || 3); n++) {
            const bookingForLane = bookingsOnDate.find(b => b.time_slot === slot.time && b.lane === n);
            const laneBlock = blockedOnDate.find(b => b.time_slot === slot.time && b.lane === n);
            lanes[n] = {
              booking: bookingForLane || null,
              blocked: laneBlock || null,
            };
          }
          // Whole-slot block (lane === null/undefined) – legacy
          const fullSlotBlock = blockedOnDate.find(b => b.time_slot === slot.time && (b.lane === null || b.lane === undefined));
          // Backward-compat single booking (without lane)
          const legacyBooking = bookingsOnDate.find(b => b.time_slot === slot.time && !b.lane);
          
          return {
            time: slot.time,
            lanes,
            fullSlotBlock: fullSlotBlock || null,
            legacyBooking: legacyBooking || null,
            booking: legacyBooking || bookingsOnDate.find(b => b.time_slot === slot.time) || null,
            blocked: fullSlotBlock || null,
            available: slot.available,
            totalLanes: slot.total_lanes || 3,
            freeLanes: slot.free_lanes || [],
          };
        });
        
        setDaySlots(slots);
      } catch (error) {
        console.error("Error fetching day slots:", error);
        setDaySlots([]);
      }
    };
    
    fetchDaySlots();
  }, [selectedDate, eisstockBookings, blockedSlots]);

  const handleLogout = () => {
    sessionStorage.removeItem("adminAuth");
    navigate("/admin");
  };

  const handleDeleteContact = (id, label) => {
    setDeleteDialog({ open: true, item: { kind: "contact", id, label: label || "Anfrage" } });
  };

  const handleDeleteSilvester = (id, label) => {
    setDeleteDialog({ open: true, item: { kind: "silvester", id, label: label || "Silvester Buchung" } });
  };

  const handleDeleteEisstock = (id, label) => {
    setDeleteDialog({ open: true, item: { kind: "eisstock", id, label: label || "Eisstock Buchung" } });
  };

  const confirmDelete = async () => {
    const item = deleteDialog.item;
    if (!item) return;

    const headers = getAuthHeaders();
    if (!headers) return;

    const endpoints = {
      contact: `/admin/contacts/${item.id}`,
      silvester: `/admin/silvester/${item.id}`,
      eisstock: `/admin/eisstock/${item.id}`,
      gutschein: `/admin/gutscheine/${item.id}`,
    };
    const url = endpoints[item.kind];
    if (!url) return;

    try {
      await axios.delete(`${API}${url}`, { headers });
      toast.success(`${item.label} gelöscht`);
      setDeleteDialog({ open: false, item: null });
      fetchData();
    } catch (error) {
      console.error(`Delete ${item.kind} failed:`, error.response || error);
      if (error.response?.status === 401) {
        sessionStorage.removeItem("adminAuth");
        toast.error("Sitzung abgelaufen. Bitte erneut anmelden.");
        setDeleteDialog({ open: false, item: null });
        navigate("/admin");
      } else {
        toast.error(
          getErrorMessage(error, `Fehler beim Löschen (${error.response?.status || "Netzwerk"})`)
        );
      }
    }
  };

  const openEditDialog = (type, data) => {
    setEditForm({
      name: data.name || "",
      email: data.email || "",
      phone: data.phone || "",
      payment_status: data.payment_status || "pending"
    });
    setEditDialog({ open: true, type, data });
  };

  const handleUpdateBooking = async () => {
    const headers = getAuthHeaders();
    if (!headers) return;

    const { type, data } = editDialog;
    const endpoint = type === "silvester" 
      ? `${API}/admin/silvester/${data.id}`
      : `${API}/admin/eisstock/${data.id}`;

    try {
      await axios.put(endpoint, editForm, { headers });
      toast.success("Buchung aktualisiert");
      setEditDialog({ open: false, type: null, data: null });
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Aktualisieren");
    }
  };

  const handleBlockSlot = async () => {
    const headers = getAuthHeaders();
    if (!headers) return;

    try {
      await axios.post(`${API}/admin/blocked-slots`, {
        date: format(blockDialog.date, "yyyy-MM-dd"),
        time_slot: blockDialog.slot,
        lane: blockDialog.lane ?? null,
        reason: blockReason || null
      }, { headers });
      toast.success(blockDialog.lane ? `Bahn ${blockDialog.lane} blockiert` : "Zeitslot blockiert");
      setBlockDialog({ open: false, date: null, slot: null, lane: null });
      setBlockReason("");
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, "Fehler beim Blockieren"));
    }
  };

  const handleUnblockSlot = async (slotId) => {
    const headers = getAuthHeaders();
    if (!headers) return;

    try {
      await axios.delete(`${API}/admin/blocked-slots/${slotId}`, { headers });
      toast.success("Blockierung aufgehoben");
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Aufheben der Blockierung");
    }
  };

  // ============ STORNO ============
  const openCancelDialog = (kind, item) => {
    const hasCapture = !!item.paypal_capture_id;
    let label = "";
    if (kind === "silvester") label = `Silvester · ${item.name} (${item.ticket_count}× ${item.ticket_type === "vip" ? "VIP" : "Normal"})`;
    else if (kind === "eisstock") label = `Eisstock · ${item.name} (${item.booking_date} ${item.time_slot}, Bahn ${item.lane || "-"})`;
    else label = `Gutschein ${item.code} (${(item.amount || 0).toFixed(2)} €)`;
    setCancelDialog({
      open: true,
      target: { kind, id: kind === "gutschein" ? item.code : item.id, label, hasCapture },
    });
    setCancelForm({ refund: hasCapture, notify_customer: true, reason: "" });
  };

  const handleCancel = async () => {
    const t = cancelDialog.target;
    if (!t) return;
    const headers = getAuthHeaders();
    if (!headers) return;

    setCancelBusy(true);
    try {
      const url = `${API}/admin/${t.kind === "gutschein" ? "gutscheine" : t.kind}/${t.id}/cancel`;
      const res = await axios.post(url, cancelForm, { headers });
      if (res.data.refunded) {
        toast.success(`Storniert + ${res.data.refund_amount?.toFixed(2)} € erstattet`);
      } else {
        toast.success("Storniert (ohne Rückzahlung)");
      }
      setCancelDialog({ open: false, target: null });
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, "Stornierung fehlgeschlagen"), { duration: 15000 });
    } finally {
      setCancelBusy(false);
    }
  };

  // ============ VIP KAPAZITÄT ============
  const handleUpdateVipCapacity = async () => {
    const headers = getAuthHeaders();
    if (!headers) return;
    const val = Number(vipCapacityInput);
    if (Number.isNaN(val) || val < 0 || val > 300) {
      toast.error("Kapazität muss zwischen 0 und 300 liegen");
      return;
    }
    setVipCapacityBusy(true);
    try {
      const res = await axios.put(`${API}/admin/silvester/capacity`, { open: val }, { headers });
      toast.success(`Kapazität auf ${res.data.capacity_open} gesetzt`);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, "Fehler beim Setzen der Kapazität"));
    } finally {
      setVipCapacityBusy(false);
    }
  };

  // ============ MANUELLE BUCHUNG ============
  const openManualDialog = (type) => {
    if (type === "silvester") {
      setManualForm({
        name: "",
        email: "",
        phone: "",
        ticket_count: 1,
        ticket_type: "vip",
        payment_mode: "cash",
        notify_customer: true,
        notify_admin: true,
      });
    } else if (type === "gutschein") {
      setManualForm({
        purchaser_name: "",
        purchaser_email: "",
        recipient_name: "",
        quantity: 1,
        amount_per_voucher: 25,
        payment_mode: "cash",
        notify_customer: true,
        notify_admin: true,
      });
    } else {
      setManualForm({
        name: "",
        email: "",
        phone: "",
        booking_date: format(selectedDate || SEASON_START, "yyyy-MM-dd"),
        time_slot: "",
        group_size: 4,
        lane: "",
        payment_mode: "cash",
        notify_customer: true,
        notify_admin: true,
      });
    }
    setManualDialog({ open: true, type });
  };

  const handleCreateManual = async () => {
    const headers = getAuthHeaders();
    if (!headers) return;
    if (manualDialog.type === "gutschein") {
      if (!manualForm.purchaser_name?.trim()) {
        toast.error("Käufer-Name ist erforderlich");
        return;
      }
    } else if (!manualForm.name?.trim()) {
      toast.error("Name ist erforderlich");
      return;
    }
    setManualBusy(true);
    try {
      const url = `${API}/admin/${manualDialog.type === "gutschein" ? "gutscheine" : manualDialog.type}/manual`;
      const payload = { ...manualForm };
      if (manualDialog.type === "eisstock") {
        if (payload.lane === "" || payload.lane === "auto") payload.lane = null;
        else payload.lane = payload.lane ? Number(payload.lane) : null;
      }
      const res = await axios.post(url, payload, { headers });
      if (manualDialog.type === "gutschein") {
        const codes = (res.data.vouchers || []).map((v) => v.code).join(", ");
        toast.success(`${res.data.quantity} Gutschein(e) angelegt: ${codes}`);
      } else if (manualDialog.type === "eisstock") {
        toast.success(`Buchung angelegt (Bahn ${res.data.lane}, Serien-Nr. ${res.data.serial_no})`);
      } else {
        toast.success(`Tickets angelegt (Serien-Nr. ${res.data.serial_no})`);
      }
      setManualDialog({ open: false, type: null });
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, "Fehler beim Anlegen"));
    } finally {
      setManualBusy(false);
    }
  };

  // ============ PAYPAL-LINK PER MAIL ============
  const openLinkDialog = (type) => {
    setLinkResult(null);
    if (type === "silvester") {
      setLinkForm({
        name: "", email: "", phone: "",
        ticket_count: 2, ticket_type: "vip", note: "",
      });
    } else if (type === "eisstock") {
      setLinkForm({
        name: "", email: "", phone: "",
        booking_date: format(selectedDate || SEASON_START, "yyyy-MM-dd"),
        time_slot: "", group_size: 4, lane: "auto", note: "",
      });
    } else {
      setLinkForm({
        purchaser_name: "", purchaser_email: "", recipient_name: "",
        quantity: 1, amount_per_voucher: 25, note: "",
      });
    }
    setLinkDialog({ open: true, type });
  };

  const handleSendPaymentLink = async () => {
    const headers = getAuthHeaders();
    if (!headers) return;
    if (linkDialog.type === "gutschein") {
      if (!linkForm.purchaser_name?.trim() || !linkForm.purchaser_email?.trim()) {
        toast.error("Käufer-Name und -E-Mail sind Pflicht");
        return;
      }
    } else if (!linkForm.name?.trim() || !linkForm.email?.trim()) {
      toast.error("Name und E-Mail sind Pflicht");
      return;
    }
    setLinkBusy(true);
    try {
      const url = `${API}/admin/payment-link/${linkDialog.type}`;
      const payload = { ...linkForm };
      if (linkDialog.type === "eisstock") {
        if (payload.lane === "" || payload.lane === "auto") payload.lane = null;
        else payload.lane = Number(payload.lane);
      }
      const res = await axios.post(url, payload, { headers });
      setLinkResult({ pay_url: res.data.pay_url, total: res.data.total });
      toast.success("Zahlungslink wurde versendet");
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, "Fehler beim Versenden"));
    } finally {
      setLinkBusy(false);
    }
  };

  const handleRedeemGutschein = async (code) => {
    const headers = getAuthHeaders();
    if (!headers) return;

    try {
      await axios.put(`${API}/admin/gutscheine/${code}/redeem`, {}, { headers });
      toast.success("Gutschein eingelöst");
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, "Fehler beim Einlösen"));
    }
  };

  const handleDeleteGutschein = (code) => {
    setDeleteDialog({ open: true, item: { kind: "gutschein", id: code, label: `Gutschein ${code}` } });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "completed":
        return <Badge className="bg-green-100 text-green-800">Bezahlt</Badge>;
      case "pending":
        return <Badge className="bg-yellow-100 text-yellow-800">Ausstehend</Badge>;
      case "cancelled":
        return <Badge className="bg-red-100 text-red-800">Storniert</Badge>;
      default:
        return <Badge className="bg-slate-100 text-slate-800">{status}</Badge>;
    }
  };

  const getCheckInBadge = (booking) => {
    if (booking.payment_status !== "completed") {
      return <span className="text-xs text-slate-400">–</span>;
    }
    if (booking.checked_in) {
      const ts = booking.checked_in_at
        ? new Date(booking.checked_in_at).toLocaleString("de-DE", {
            day: "2-digit",
            month: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
          })
        : "";
      return (
        <Badge className="bg-green-100 text-green-800 gap-1">
          <CheckCircle2 className="w-3 h-3" />
          <span className="font-normal">{ts || "Ja"}</span>
        </Badge>
      );
    }
    return <Badge className="bg-sky-100 text-sky-800">Offen</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch {
      return dateStr;
    }
  };

  // Stats
  const pendingSilvester = silvesterBookings.filter(b => b.payment_status === "pending").length;
  const completedSilvester = silvesterBookings.filter(b => b.payment_status === "completed").length;
  const pendingEisstock = eisstockBookings.filter(b => b.payment_status === "pending").length;
  const completedEisstock = eisstockBookings.filter(b => b.payment_status === "completed").length;

  // Check-in stats (only count completed/paid bookings)
  const checkedInSilvester = silvesterBookings.filter(b => b.payment_status === "completed" && b.checked_in).length;
  const openSilvester = completedSilvester - checkedInSilvester;
  const checkedInEisstock = eisstockBookings.filter(b => b.payment_status === "completed" && b.checked_in).length;
  const openEisstock = completedEisstock - checkedInEisstock;
  const totalCheckedIn = checkedInSilvester + checkedInEisstock;
  const totalOpen = openSilvester + openEisstock;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Snowflake className="w-8 h-8 text-sky-500" />
            <div>
              <h1 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Playfair Display' }}>
                Admin Dashboard
              </h1>
              <p className="text-sm text-slate-500">Grömitzer Eiszeit</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button
              variant="default"
              size="sm"
              onClick={() => navigate("/checkin")}
              className="gap-2 bg-green-500 hover:bg-green-600"
              data-testid="checkin-btn"
            >
              <QrCode className="w-4 h-4" />
              Check-in
            </Button>
            <Button
              size="sm"
              onClick={() => navigate("/admin/tagesplan")}
              className="gap-2 bg-sky-500 hover:bg-sky-600"
              data-testid="open-tagesplan-btn"
            >
              <CalendarIcon className="w-4 h-4" />
              Tagesplan
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchData}
              disabled={loading}
              className="gap-2"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Aktualisieren
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="gap-2 text-slate-600"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              Abmelden
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="border-0 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Anfragen</p>
                  <p className="text-2xl font-bold text-slate-900">{contacts.length}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <Mail className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Silvester</p>
                  <p className="text-2xl font-bold text-slate-900">{silvesterBookings.length}</p>
                  <p className="text-xs text-green-600">{completedSilvester} bezahlt</p>
                  {completedSilvester > 0 && (
                    <p className="text-xs text-sky-600">
                      {checkedInSilvester} eingecheckt · {openSilvester} offen
                    </p>
                  )}
                </div>
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                  <Ticket className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Eisstock</p>
                  <p className="text-2xl font-bold text-slate-900">{eisstockBookings.length}</p>
                  <p className="text-xs text-green-600">{completedEisstock} bezahlt</p>
                  {completedEisstock > 0 && (
                    <p className="text-xs text-sky-600">
                      {checkedInEisstock} eingecheckt · {openEisstock} offen
                    </p>
                  )}
                </div>
                <div className="w-12 h-12 rounded-full bg-sky-100 flex items-center justify-center">
                  <Users className="w-6 h-6 text-sky-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Blockiert</p>
                  <p className="text-2xl font-bold text-slate-900">{blockedSlots.length}</p>
                  <p className="text-xs text-slate-500">Zeitslots</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <Lock className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Check-in Statistik Widget */}
        {(completedSilvester > 0 || completedEisstock > 0) && (
          <div className="mb-8" data-testid="checkin-stats-widget">
            <Card className="border-0 shadow-sm bg-gradient-to-r from-sky-50 to-blue-50">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-full bg-white shadow flex items-center justify-center flex-shrink-0">
                      <QrCode className="w-7 h-7 text-sky-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Playfair Display' }}>
                        Check-in Status
                      </h3>
                      <p className="text-sm text-slate-500">
                        Übersicht aller bezahlten Tickets · Stand: jetzt
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-6 md:gap-10">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-green-600" data-testid="stat-checked-in">
                        {totalCheckedIn}
                      </div>
                      <div className="text-xs uppercase tracking-wide text-slate-500 mt-1">
                        Eingecheckt
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-amber-600" data-testid="stat-open">
                        {totalOpen}
                      </div>
                      <div className="text-xs uppercase tracking-wide text-slate-500 mt-1">
                        Noch offen
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-slate-900" data-testid="stat-total-paid">
                        {completedSilvester + completedEisstock}
                      </div>
                      <div className="text-xs uppercase tracking-wide text-slate-500 mt-1">
                        Gesamt bezahlt
                      </div>
                    </div>
                  </div>
                </div>

                {/* Progress bar */}
                {(completedSilvester + completedEisstock) > 0 && (
                  <div className="mt-5">
                    <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-green-500 to-sky-500 transition-all"
                        style={{
                          width: `${Math.round((totalCheckedIn / (completedSilvester + completedEisstock)) * 100)}%`,
                        }}
                      />
                    </div>
                    <p className="text-xs text-slate-500 mt-2 text-right">
                      {Math.round((totalCheckedIn / (completedSilvester + completedEisstock)) * 100)}% eingecheckt
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Live-Buchung-Quick-Links + PayPal-Link-per-Mail */}
        <Card className="border-0 shadow-sm mb-6 bg-gradient-to-r from-sky-50 to-emerald-50">
          <CardContent className="py-4 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="text-sm font-semibold text-slate-900">Live-Buchung starten</p>
                <p className="text-xs text-slate-600 mt-0.5">
                  Öffnet den öffentlichen Buchungsflow inkl. PayPal-Zahlung (für eigene Bestellungen oder am Telefon vorzulesen)
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <a href="/silvester?type=vip" target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-1 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold px-3 py-2 transition"
                   data-testid="live-buy-silvester-vip">
                  <Ticket className="w-3.5 h-3.5" /> Silvester VIP
                </a>
                <a href="/silvester?type=normal" target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-1 rounded-lg bg-slate-600 hover:bg-slate-700 text-white text-xs font-semibold px-3 py-2 transition"
                   data-testid="live-buy-silvester-normal">
                  <Ticket className="w-3.5 h-3.5" /> Silvester Normal
                </a>
                <a href="/eisstockschiessen" target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-1 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-xs font-semibold px-3 py-2 transition"
                   data-testid="live-buy-eisstock">
                  <Users className="w-3.5 h-3.5" /> Eisstockschießen
                </a>
                <a href="/gutschein" target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-1 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold px-3 py-2 transition"
                   data-testid="live-buy-gutschein">
                  <Gift className="w-3.5 h-3.5" /> Gutschein
                </a>
              </div>
            </div>
            <div className="border-t border-slate-200/70 pt-3 flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="text-sm font-semibold text-slate-900">PayPal-Link per E-Mail senden</p>
                <p className="text-xs text-slate-600 mt-0.5">
                  Telefonbestellung erfassen → Kunde bekommt Zahlungslink per Mail (gültig 72 h)
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => openLinkDialog("silvester")}
                        className="border-amber-300 text-amber-700 hover:bg-amber-50"
                        data-testid="link-silvester-btn">
                  <Mail className="w-3.5 h-3.5 mr-1" /> Silvester
                </Button>
                <Button size="sm" variant="outline" onClick={() => openLinkDialog("eisstock")}
                        className="border-sky-300 text-sky-700 hover:bg-sky-50"
                        data-testid="link-eisstock-btn">
                  <Mail className="w-3.5 h-3.5 mr-1" /> Eisstock
                </Button>
                <Button size="sm" variant="outline" onClick={() => openLinkDialog("gutschein")}
                        className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                        data-testid="link-gutschein-btn">
                  <Mail className="w-3.5 h-3.5 mr-1" /> Gutschein
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); if (v === "rechnungen") fetchInvoiceAudit(); }} className="space-y-6">
          <TabsList className="bg-white border shadow-sm">
            <TabsTrigger value="anfragen" className="gap-2" data-testid="tab-anfragen">
              <Mail className="w-4 h-4" />
              Anfragen ({contacts.length})
            </TabsTrigger>
            <TabsTrigger value="silvester" className="gap-2" data-testid="tab-silvester">
              <Ticket className="w-4 h-4" />
              Silvester ({silvesterBookings.length})
            </TabsTrigger>
            <TabsTrigger value="eisstock" className="gap-2" data-testid="tab-eisstock">
              <Users className="w-4 h-4" />
              Eisstock ({eisstockBookings.length})
            </TabsTrigger>
            <TabsTrigger value="kalender" className="gap-2" data-testid="tab-kalender">
              <CalendarIcon className="w-4 h-4" />
              Kalender
            </TabsTrigger>
            <TabsTrigger value="gutscheine" className="gap-2" data-testid="tab-gutscheine">
              <Gift className="w-4 h-4" />
              Gutscheine ({gutscheine.length})
            </TabsTrigger>
            <TabsTrigger value="rechnungen" className="gap-2" data-testid="tab-rechnungen">
              <FileText className="w-4 h-4" />
              Rechnungsbuch
            </TabsTrigger>
          </TabsList>

          {/* Anfragen Tab */}
          <TabsContent value="anfragen">
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg">Kontaktanfragen</CardTitle>
              </CardHeader>
              <CardContent>
                {contacts.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">Keine Anfragen vorhanden</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Datum</TableHead>
                          <TableHead>Name</TableHead>
                          <TableHead>E-Mail</TableHead>
                          <TableHead>Telefon</TableHead>
                          <TableHead>Nachricht</TableHead>
                          <TableHead className="w-20">Aktion</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {contacts.map((contact) => (
                          <TableRow key={contact.id}>
                            <TableCell className="text-sm">{formatDate(contact.created_at)}</TableCell>
                            <TableCell className="font-medium">{contact.name}</TableCell>
                            <TableCell>
                              <a href={`mailto:${contact.email}`} className="text-sky-600 hover:underline">
                                {contact.email}
                              </a>
                            </TableCell>
                            <TableCell>
                              <a href={`tel:${contact.phone}`} className="text-sky-600 hover:underline">
                                {contact.phone}
                              </a>
                            </TableCell>
                            <TableCell className="max-w-xs truncate">{contact.message}</TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteContact(contact.id, `Anfrage · ${contact.name}`)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                data-testid={`delete-contact-${contact.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Silvester Tab */}
          <TabsContent value="silvester">
            {/* VIP Sitzplatz-Übersicht */}
            <Card className="border-0 shadow-sm mb-6">
              <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-3">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Ticket className="w-5 h-5 text-amber-500" /> VIP-Sitzplatz-Kontingent
                  </CardTitle>
                  <p className="text-xs text-slate-500 mt-1">
                    Insgesamt 300 Plätze · standardmäßig 250 freigegeben · 50 Reserve nach Bedarf freischalten
                  </p>
                </div>
                {vipSeatsData && (
                  <div className="flex items-center gap-3 text-right" data-testid="vip-summary">
                    <div>
                      <p className="text-xs text-slate-500">belegt</p>
                      <p className="text-2xl font-bold text-amber-600">
                        {vipSeatsData.occupied}<span className="text-slate-400 text-base">/{vipSeatsData.capacity_open}</span>
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">noch frei</p>
                      <p className="text-2xl font-bold text-emerald-600">{vipSeatsData.free}</p>
                    </div>
                    <a
                      href={`${API}/admin/silvester/sitzplan/pdf`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => {
                        // Need to attach Basic Auth – fetch as blob and open
                        e.preventDefault();
                        const headers = getAuthHeaders();
                        if (!headers) return;
                        fetch(`${API}/admin/silvester/sitzplan/pdf`, { headers })
                          .then((r) => r.ok ? r.blob() : Promise.reject(r))
                          .then((blob) => {
                            const url = URL.createObjectURL(blob);
                            window.open(url, "_blank");
                            setTimeout(() => URL.revokeObjectURL(url), 60000);
                          })
                          .catch(() => toast.error("Sitzplan-PDF konnte nicht geladen werden"));
                      }}
                      data-testid="sitzplan-pdf-btn"
                      className="inline-flex items-center gap-1 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold px-3 py-2 transition"
                    >
                      <QrCode className="w-3.5 h-3.5" />
                      Sitzplan-PDF
                    </a>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                {vipSeatsData && (
                  <>
                    {/* Auslastungs-Bar */}
                    <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden mb-4">
                      <div
                        className="h-full bg-gradient-to-r from-amber-400 to-amber-600 transition-all"
                        style={{ width: `${vipSeatsData.capacity_open ? Math.round((vipSeatsData.occupied / vipSeatsData.capacity_open) * 100) : 0}%` }}
                      />
                    </div>

                    {/* Capacity Slider */}
                    <div className="bg-slate-50 rounded-xl p-4 mb-4">
                      <div className="flex items-center justify-between flex-wrap gap-3">
                        <div>
                          <Label className="text-sm font-semibold">Aktuell freigegebene Plätze</Label>
                          <p className="text-xs text-slate-500">Schieber zwischen 0 und 300 (250 Standard, 50 Reserve)</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <input
                            type="range"
                            min={0}
                            max={300}
                            value={vipCapacityInput}
                            onChange={(e) => setVipCapacityInput(Number(e.target.value))}
                            className="w-48"
                            data-testid="vip-capacity-slider"
                          />
                          <Input
                            type="number"
                            min={0}
                            max={300}
                            value={vipCapacityInput}
                            onChange={(e) => setVipCapacityInput(Number(e.target.value))}
                            className="w-24"
                            data-testid="vip-capacity-input"
                          />
                          <Button
                            onClick={handleUpdateVipCapacity}
                            disabled={vipCapacityBusy || vipCapacityInput === vipSeatsData.capacity_open}
                            className="bg-amber-500 hover:bg-amber-600"
                            data-testid="vip-capacity-save"
                          >
                            {vipCapacityBusy ? "…" : "Speichern"}
                          </Button>
                        </div>
                      </div>
                      {vipCapacityInput !== vipSeatsData.capacity_open && (
                        <p className="text-xs text-amber-700 mt-2">
                          ⚠️ Ungespeicherte Änderung: {vipSeatsData.capacity_open} → {vipCapacityInput}
                        </p>
                      )}
                    </div>

                    {/* Sitzplatz-Belegung-Liste */}
                    <details className="bg-slate-50 rounded-xl p-4">
                      <summary className="cursor-pointer text-sm font-semibold text-slate-700 select-none">
                        Sitzplatz-Belegung anzeigen ({vipSeatsData.bookings?.length || 0} Buchungen)
                      </summary>
                      <div className="mt-3 space-y-2 max-h-64 overflow-y-auto">
                        {(vipSeatsData.bookings || []).length === 0 ? (
                          <p className="text-sm text-slate-500 italic">Noch keine VIP-Buchungen</p>
                        ) : (
                          [...vipSeatsData.bookings]
                            .sort((a, b) => {
                              const sa = (a.seat_numbers || [])[0] ?? 9999;
                              const sb = (b.seat_numbers || [])[0] ?? 9999;
                              return sa - sb;
                            })
                            .map((b) => {
                              const seats = [...(b.seat_numbers || [])].sort((x, y) => x - y);
                              const range =
                                seats.length === 0
                                  ? "–"
                                  : seats.length > 1 && seats[seats.length - 1] - seats[0] === seats.length - 1
                                  ? `${seats[0]}–${seats[seats.length - 1]}`
                                  : seats.join(", ");
                              return (
                                <div
                                  key={b.id}
                                  className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-slate-200"
                                  data-testid={`vip-seat-row-${b.id}`}
                                >
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold text-slate-900 truncate">{b.name}</p>
                                    <p className="text-xs text-slate-500">{b.ticket_count} Tickets · {b.payment_status}</p>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-xs text-slate-500">Sitz</p>
                                    <p className="font-mono text-amber-700 font-semibold">{range}</p>
                                  </div>
                                </div>
                              );
                            })
                        )}
                      </div>
                    </details>
                  </>
                )}
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Silvester Buchungen</CardTitle>
                <Button
                  size="sm"
                  onClick={() => openManualDialog("silvester")}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="manual-silvester-btn"
                >
                  <Plus className="w-4 h-4 mr-1" /> Manuelle Buchung
                </Button>
              </CardHeader>
              <CardContent>
                {silvesterBookings.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">Keine Buchungen vorhanden</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Datum</TableHead>
                          <TableHead>Serien-Nr.</TableHead>
                          <TableHead>Name</TableHead>
                          <TableHead>E-Mail</TableHead>
                          <TableHead>Telefon</TableHead>
                          <TableHead>Typ</TableHead>
                          <TableHead>Anzahl</TableHead>
                          <TableHead>Sitze</TableHead>
                          <TableHead>Preis</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Check-in</TableHead>
                          <TableHead className="w-24">Aktionen</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {silvesterBookings.map((booking) => {
                          const seats = [...(booking.seat_numbers || [])].sort((a, b) => a - b);
                          const seatRange =
                            booking.ticket_type !== "vip"
                              ? "–"
                              : seats.length === 0
                              ? "–"
                              : seats.length > 1 && seats[seats.length - 1] - seats[0] === seats.length - 1
                              ? `${seats[0]}–${seats[seats.length - 1]}`
                              : seats.join(", ");
                          return (
                          <TableRow key={booking.id}>
                            <TableCell className="text-sm">{formatDate(booking.created_at)}</TableCell>
                            <TableCell className="font-mono text-xs text-slate-700" data-testid={`silvester-serial-${booking.id}`}>
                              {booking.serial_no || "–"}
                            </TableCell>
                            <TableCell className="font-medium">{booking.name}</TableCell>
                            <TableCell>
                              <a href={`mailto:${booking.email}`} className="text-sky-600 hover:underline">
                                {booking.email}
                              </a>
                            </TableCell>
                            <TableCell>{booking.phone}</TableCell>
                            <TableCell>
                              <Badge className={booking.ticket_type === "vip" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-800"}>
                                {booking.ticket_type === "vip" ? "VIP" : "Normal"}
                              </Badge>
                            </TableCell>
                            <TableCell>{booking.ticket_count}</TableCell>
                            <TableCell className="font-mono text-amber-700 text-sm" data-testid={`silvester-seats-${booking.id}`}>
                              {seatRange}
                            </TableCell>
                            <TableCell className="font-medium">{booking.total_price?.toFixed(2)} €</TableCell>
                            <TableCell>{getStatusBadge(booking.payment_status)}</TableCell>
                            <TableCell data-testid={`silvester-checkin-${booking.id}`}>
                              {getCheckInBadge(booking)}
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openEditDialog("silvester", booking)}
                                  className="text-slate-600 hover:text-slate-700"
                                  data-testid={`edit-silvester-${booking.id}`}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                {booking.payment_status === "completed" && (booking.total_price || 0) > 0 && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => downloadInvoice("silvester", booking.id, booking.serial_no)}
                                    className="text-sky-600 hover:text-sky-700 hover:bg-sky-50"
                                    title="Rechnung herunterladen"
                                    data-testid={`invoice-silvester-${booking.id}`}
                                  >
                                    <FileText className="w-4 h-4" />
                                  </Button>
                                )}
                                {booking.payment_status !== "pending" && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => downloadTicket("silvester", booking.id)}
                                    className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                    title="Ticket-PDF herunterladen (zum erneuten Zusenden an den Kunden)"
                                    data-testid={`ticket-silvester-${booking.id}`}
                                  >
                                    <Ticket className="w-4 h-4" />
                                  </Button>
                                )}
                                {(booking.payment_status === "cancelled" || booking.payment_status === "refunded") && booking.invoice_no && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => downloadCreditNote("silvester", booking.id)}
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    title="Stornorechnung herunterladen"
                                    data-testid={`credit-note-silvester-${booking.id}`}
                                  >
                                    <FileText className="w-4 h-4" />
                                  </Button>
                                )}
                                {booking.payment_status !== "cancelled" && booking.payment_status !== "refunded" && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openCancelDialog("silvester", booking)}
                                    className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                    title="Stornieren / Rückzahlung"
                                    data-testid={`cancel-silvester-${booking.id}`}
                                  >
                                    <Ban className="w-4 h-4" />
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteSilvester(booking.id, `Silvester · ${booking.name}`)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  data-testid={`delete-silvester-${booking.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Eisstock Tab */}
          <TabsContent value="eisstock">
            <Card className="border-0 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Eisstockschießen Buchungen</CardTitle>
                <Button
                  size="sm"
                  onClick={() => openManualDialog("eisstock")}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="manual-eisstock-btn"
                >
                  <Plus className="w-4 h-4 mr-1" /> Manuelle Buchung
                </Button>
              </CardHeader>
              <CardContent>
                {eisstockBookings.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">Keine Buchungen vorhanden</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Buchungsdatum</TableHead>
                          <TableHead>Serien-Nr.</TableHead>
                          <TableHead>Termin</TableHead>
                          <TableHead>Zeitslot</TableHead>
                          <TableHead>Name</TableHead>
                          <TableHead>E-Mail</TableHead>
                          <TableHead>Telefon</TableHead>
                          <TableHead>Personen</TableHead>
                          <TableHead>Preis</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Check-in</TableHead>
                          <TableHead className="w-24">Aktionen</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {eisstockBookings.map((booking) => (
                          <TableRow key={booking.id}>
                            <TableCell className="text-sm">{formatDate(booking.created_at)}</TableCell>
                            <TableCell className="font-mono text-xs text-slate-700" data-testid={`eisstock-serial-${booking.id}`}>
                              {booking.serial_no || "–"}
                            </TableCell>
                            <TableCell className="font-medium">{booking.booking_date}</TableCell>
                            <TableCell>
                              <Badge className="bg-sky-100 text-sky-800">
                                {booking.time_slot}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-medium">{booking.name}</TableCell>
                            <TableCell>
                              <a href={`mailto:${booking.email}`} className="text-sky-600 hover:underline">
                                {booking.email}
                              </a>
                            </TableCell>
                            <TableCell>{booking.phone}</TableCell>
                            <TableCell>{booking.group_size}</TableCell>
                            <TableCell className="font-medium">{booking.total_price?.toFixed(2)} €</TableCell>
                            <TableCell>{getStatusBadge(booking.payment_status)}</TableCell>
                            <TableCell data-testid={`eisstock-checkin-${booking.id}`}>
                              {getCheckInBadge(booking)}
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openEditDialog("eisstock", booking)}
                                  className="text-slate-600 hover:text-slate-700"
                                  data-testid={`edit-eisstock-${booking.id}`}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                {booking.payment_status === "completed" && (booking.total_price || 0) > 0 && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => downloadInvoice("eisstock", booking.id, booking.serial_no)}
                                    className="text-sky-600 hover:text-sky-700 hover:bg-sky-50"
                                    title="Rechnung herunterladen"
                                    data-testid={`invoice-eisstock-${booking.id}`}
                                  >
                                    <FileText className="w-4 h-4" />
                                  </Button>
                                )}
                                {booking.payment_status !== "pending" && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => downloadTicket("eisstock", booking.id)}
                                    className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                    title="Ticket-PDF herunterladen (zum erneuten Zusenden an den Kunden)"
                                    data-testid={`ticket-eisstock-${booking.id}`}
                                  >
                                    <Ticket className="w-4 h-4" />
                                  </Button>
                                )}
                                {(booking.payment_status === "cancelled" || booking.payment_status === "refunded") && booking.invoice_no && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => downloadCreditNote("eisstock", booking.id)}
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    title="Stornorechnung herunterladen"
                                    data-testid={`credit-note-eisstock-${booking.id}`}
                                  >
                                    <FileText className="w-4 h-4" />
                                  </Button>
                                )}
                                {booking.payment_status !== "cancelled" && booking.payment_status !== "refunded" && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openCancelDialog("eisstock", booking)}
                                    className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                    title="Stornieren / Rückzahlung"
                                    data-testid={`cancel-eisstock-${booking.id}`}
                                  >
                                    <Ban className="w-4 h-4" />
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteEisstock(booking.id, `Eisstock · ${booking.name}`)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  data-testid={`delete-eisstock-${booking.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Kalender Tab */}
          <TabsContent value="kalender">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Calendar */}
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg">Datum auswählen</CardTitle>
                  <p className="text-sm text-slate-500">Saison: 27.11.2026 - 06.01.2027</p>
                </CardHeader>
                <CardContent>
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => date && setSelectedDate(date)}
                    locale={de}
                    defaultMonth={SEASON_START}
                    className="rounded-md border"
                    data-testid="admin-calendar"
                  />
                </CardContent>
              </Card>

              {/* Day View */}
              <Card className="border-0 shadow-sm lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <CalendarIcon className="w-5 h-5 text-sky-500" />
                    {format(selectedDate, "EEEE, d. MMMM yyyy", { locale: de })}
                  </CardTitle>
                  {openingHours && (
                    <p className="text-sm text-sky-600 flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      Öffnungszeiten: {openingHours}
                    </p>
                  )}
                </CardHeader>
                <CardContent>
                  {!isInSeason ? (
                    <div className="text-center py-12 bg-amber-50 rounded-xl border border-amber-200">
                      <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
                      <p className="font-semibold text-amber-800">Außerhalb der Saison</p>
                      <p className="text-amber-600 text-sm mt-1">
                        Dieses Datum liegt außerhalb der Saison (27.11.2026 - 06.01.2027)
                      </p>
                    </div>
                  ) : daySlots.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      Keine Zeitslots für diesen Tag
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {daySlots.map((slot) => {
                        const isFullSlotBlocked = !!slot.fullSlotBlock;
                        return (
                        <div
                          key={slot.time}
                          className={`p-4 rounded-xl border-2 ${
                            isFullSlotBlocked
                              ? "border-red-300 bg-red-50"
                              : "border-slate-200 bg-white"
                          }`}
                        >
                          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                            <div className="flex items-center gap-3">
                              <Clock className="w-5 h-5 text-slate-400" />
                              <span className="font-semibold text-slate-900">{slot.time}</span>
                              {isFullSlotBlocked && (
                                <Badge className="bg-red-100 text-red-700 border-red-200">
                                  ganzer Slot blockiert{slot.fullSlotBlock.reason ? `: ${slot.fullSlotBlock.reason}` : ""}
                                </Badge>
                              )}
                            </div>
                            {isFullSlotBlocked ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleUnblockSlot(slot.fullSlotBlock.id)}
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                data-testid={`unblock-full-${slot.time}`}
                              >
                                <Unlock className="w-4 h-4 mr-1" /> Slot freigeben
                              </Button>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setBlockDialog({ open: true, date: selectedDate, slot: slot.time, lane: null })}
                                className="text-slate-500 hover:text-red-600"
                                data-testid={`block-full-${slot.time}`}
                              >
                                <Lock className="w-4 h-4 mr-1" /> ganzen Slot sperren
                              </Button>
                            )}
                          </div>

                          {/* Per-lane grid */}
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                            {[1, 2, 3].map((laneNum) => {
                              const lane = slot.lanes?.[laneNum] || {};
                              const booking = lane.booking;
                              const blocked = lane.blocked;
                              return (
                                <div
                                  key={laneNum}
                                  className={`p-3 rounded-lg border ${
                                    booking
                                      ? "border-sky-300 bg-sky-50"
                                      : blocked
                                        ? "border-red-300 bg-red-50"
                                        : isFullSlotBlocked
                                          ? "border-slate-200 bg-slate-50 opacity-60"
                                          : "border-emerald-200 bg-emerald-50"
                                  }`}
                                  data-testid={`lane-${slot.time}-${laneNum}`}
                                >
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="text-xs font-bold uppercase tracking-wider text-slate-700">
                                      Bahn {laneNum}
                                    </span>
                                    {booking ? (
                                      getStatusBadge(booking.payment_status)
                                    ) : blocked ? (
                                      <Badge className="bg-red-100 text-red-700 border-red-200">Blockiert</Badge>
                                    ) : !isFullSlotBlocked ? (
                                      <Badge className="bg-emerald-100 text-emerald-700">Frei</Badge>
                                    ) : null}
                                  </div>

                                  {booking ? (
                                    <div className="flex items-center justify-between">
                                      <div className="min-w-0">
                                        <p className="text-sm font-medium text-slate-900 truncate">{booking.name}</p>
                                        <p className="text-xs text-slate-500">{booking.group_size} Pers.</p>
                                      </div>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => openEditDialog("eisstock", booking)}
                                        className="text-slate-600 ml-1 h-8 w-8 p-0"
                                      >
                                        <Edit className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  ) : blocked ? (
                                    <div className="flex items-center justify-between">
                                      <span className="text-xs text-red-700 truncate">
                                        {blocked.reason || "Manuell blockiert"}
                                      </span>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleUnblockSlot(blocked.id)}
                                        className="text-green-600 hover:bg-green-100 h-8 w-8 p-0"
                                        data-testid={`unblock-lane-${slot.time}-${laneNum}`}
                                      >
                                        <Unlock className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  ) : !isFullSlotBlocked ? (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => setBlockDialog({ open: true, date: selectedDate, slot: slot.time, lane: laneNum })}
                                      className="text-xs text-slate-500 hover:text-red-600 h-7 w-full justify-start px-1"
                                      data-testid={`block-lane-${slot.time}-${laneNum}`}
                                    >
                                      <Lock className="w-3 h-3 mr-1" /> blockieren
                                    </Button>
                                  ) : (
                                    <span className="text-xs text-slate-400">– gesperrt –</span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Gutscheine Tab */}
          <TabsContent value="gutscheine">
            <Card className="border-0 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-3">
                <div>
                  <CardTitle className="text-lg">Gutscheine</CardTitle>
                  <p className="text-sm text-slate-500 mt-1">
                    {gutscheine.filter(g => g.payment_status === "completed" && !g.redeemed).length} offen · {" "}
                    {gutscheine.filter(g => g.redeemed).length} eingelöst · {" "}
                    Gesamtwert: {gutscheine.filter(g => g.payment_status === "completed").reduce((s, g) => s + (g.amount || 0), 0).toFixed(2)} €
                  </p>
                </div>
                <Button
                  size="sm"
                  onClick={() => openManualDialog("gutschein")}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="manual-gutschein-btn"
                >
                  <Plus className="w-4 h-4 mr-1" /> Manueller Gutschein
                </Button>
              </CardHeader>
              <CardContent>
                {gutscheine.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">Keine Gutscheine vorhanden</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Datum</TableHead>
                          <TableHead>Code</TableHead>
                          <TableHead>Wert</TableHead>
                          <TableHead>Käufer</TableHead>
                          <TableHead>Empfänger</TableHead>
                          <TableHead>Zahlung</TableHead>
                          <TableHead>Einlösung</TableHead>
                          <TableHead className="text-right">Aktionen</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {gutscheine
                          .slice()
                          .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))
                          .map((g) => (
                            <TableRow key={g.code} data-testid={`gutschein-row-${g.code}`}>
                              <TableCell className="text-sm text-slate-500">
                                {g.created_at ? format(new Date(g.created_at), "dd.MM.yy") : "-"}
                              </TableCell>
                              <TableCell className="font-mono text-xs">{g.code}</TableCell>
                              <TableCell className="font-semibold">{(g.amount || 0).toFixed(2)} €</TableCell>
                              <TableCell>
                                <div className="text-sm">{g.purchaser_name}</div>
                                <div className="text-xs text-slate-500">{g.purchaser_email}</div>
                              </TableCell>
                              <TableCell className="text-sm">{g.recipient_name || "-"}</TableCell>
                              <TableCell>{getStatusBadge(g.payment_status)}</TableCell>
                              <TableCell>
                                {g.redeemed ? (
                                  <Badge className="bg-slate-200 text-slate-700">
                                    <CheckCircle2 className="w-3 h-3 mr-1" />
                                    Eingelöst
                                  </Badge>
                                ) : g.payment_status === "completed" ? (
                                  <Badge className="bg-sky-100 text-sky-800">Offen</Badge>
                                ) : (
                                  <span className="text-xs text-slate-400">–</span>
                                )}
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex justify-end gap-2">
                                  {g.payment_status === "completed" && (
                                    <a
                                      href={`${API}/gutschein/pdf/${g.code}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      <Button size="sm" variant="outline" data-testid={`admin-pdf-${g.code}`}>
                                        PDF
                                      </Button>
                                    </a>
                                  )}
                                  {g.payment_status === "completed" && (g.amount || 0) > 0 && g.batch_id && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="text-sky-600 hover:text-sky-700"
                                      onClick={() => downloadInvoice("gutschein", g.batch_id, g.invoice_no || g.batch_id?.slice(0,8))}
                                      title="Rechnung herunterladen"
                                      data-testid={`admin-invoice-gutschein-${g.code}`}
                                    >
                                      <FileText className="w-3 h-3 mr-1" />
                                      Rechnung
                                    </Button>
                                  )}
                                  {(g.payment_status === "cancelled" || g.payment_status === "refunded") && g.invoice_no && g.batch_id && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="text-red-600 hover:text-red-700"
                                      onClick={() => downloadCreditNote("gutschein", g.batch_id)}
                                      title="Stornorechnung herunterladen"
                                      data-testid={`admin-credit-note-gutschein-${g.code}`}
                                    >
                                      <FileText className="w-3 h-3 mr-1" />
                                      Storno
                                    </Button>
                                  )}
                                  {g.payment_status === "completed" && !g.redeemed && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleRedeemGutschein(g.code)}
                                      data-testid={`admin-redeem-${g.code}`}
                                    >
                                      <CheckCircle2 className="w-3 h-3 mr-1" />
                                      Einlösen
                                    </Button>
                                  )}
                                  {g.payment_status === "completed" && !g.redeemed && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="text-amber-600 hover:text-amber-700"
                                      onClick={() => openCancelDialog("gutschein", g)}
                                      title="Stornieren / Rückzahlung"
                                      data-testid={`admin-cancel-gutschein-${g.code}`}
                                    >
                                      <Ban className="w-3 h-3" />
                                    </Button>
                                  )}
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-red-600 hover:text-red-700"
                                    onClick={() => handleDeleteGutschein(g.code)}
                                    data-testid={`admin-delete-gutschein-${g.code}`}
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Rechnungsbuch Tab */}
          <TabsContent value="rechnungen">
            <Card className="border-0 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileText className="w-5 h-5 text-sky-600" />
                    Rechnungsbuch (§14 UStG)
                  </CardTitle>
                  <p className="text-sm text-slate-500 mt-1">Fortlaufende Rechnungs- und Stornonummern – Lücken werden automatisch erkannt.</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchInvoiceAudit}
                  disabled={invoiceAuditLoading}
                  data-testid="invoice-audit-refresh-btn"
                  className="gap-2"
                >
                  <RefreshCw className={`w-4 h-4 ${invoiceAuditLoading ? "animate-spin" : ""}`} />
                  Aktualisieren
                </Button>
              </CardHeader>
              <CardContent>
                {!invoiceAudit ? (
                  <p className="text-sm text-slate-500">Lade Rechnungsbuch…</p>
                ) : (
                  <>
                    {invoiceAudit.gaps && invoiceAudit.gaps.length > 0 && (
                      <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4" data-testid="invoice-audit-gaps">
                        <div className="flex items-start gap-2">
                          <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                          <div className="text-sm text-amber-900">
                            <p className="font-semibold">Lücken in der Nummernfolge erkannt</p>
                            {invoiceAudit.gaps.map((g) => (
                              <p key={g.year} className="mt-1 text-xs">
                                <span className="font-mono">{g.range_min} … {g.range_max}</span> –
                                fehlend: <span className="font-mono">{g.missing.join(", ")}</span>
                              </p>
                            ))}
                            <p className="mt-2 text-xs text-amber-800">
                              Lücken können entstehen, wenn eine Rechnung angelegt aber die PDF-Erzeugung fehlschlug
                              oder wenn historische Buchungen vor Einführung des Rechnungsbuchs gelöscht wurden.
                              Für die Buchführung kannst du diese Nummern als „nicht erteilt&ldquo; dokumentieren.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="text-sm text-slate-500 mb-3" data-testid="invoice-audit-total">
                      {invoiceAudit.total} Einträge gesamt
                    </div>
                    {invoiceAudit.records.length === 0 ? (
                      <p className="text-sm text-slate-400">Noch keine Rechnungen erfasst.</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>RE-Nr.</TableHead>
                              <TableHead>Datum</TableHead>
                              <TableHead>Art</TableHead>
                              <TableHead>Kunde</TableHead>
                              <TableHead className="text-right">Betrag (brutto)</TableHead>
                              <TableHead>Zahlung</TableHead>
                              <TableHead>Bezug</TableHead>
                              <TableHead>Status</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {invoiceAudit.records.map((r) => {
                              const isStorno = (r.kind || "").startsWith("storno_");
                              const baseKind = isStorno ? r.kind.replace("storno_", "") : r.kind;
                              return (
                                <TableRow key={r.invoice_no} data-testid={`audit-row-${r.invoice_no}`}>
                                  <TableCell className="font-mono text-xs">{r.invoice_no}</TableCell>
                                  <TableCell className="text-sm text-slate-500">
                                    {r.created_at ? new Date(r.created_at).toLocaleDateString("de-DE") : "-"}
                                  </TableCell>
                                  <TableCell>
                                    {isStorno ? (
                                      <Badge className="bg-red-100 text-red-800">Storno · {baseKind}</Badge>
                                    ) : (
                                      <Badge className="bg-sky-100 text-sky-800">{baseKind}</Badge>
                                    )}
                                  </TableCell>
                                  <TableCell className="text-sm">
                                    <div>{r.customer_name || "-"}</div>
                                    {r.customer_email && (
                                      <div className="text-xs text-slate-500">{r.customer_email}</div>
                                    )}
                                  </TableCell>
                                  <TableCell className={`text-right font-medium ${(r.gross_total || 0) < 0 ? "text-red-600" : "text-slate-900"}`}>
                                    {(r.gross_total || 0).toFixed(2)} €
                                  </TableCell>
                                  <TableCell className="text-xs text-slate-600">{r.payment_mode || "-"}</TableCell>
                                  <TableCell className="font-mono text-xs text-slate-500">
                                    {r.references_invoice_no || "-"}
                                  </TableCell>
                                  <TableCell>
                                    {r.status === "issued" ? (
                                      <Badge className="bg-green-100 text-green-800">erteilt</Badge>
                                    ) : (
                                      <Badge className="bg-red-100 text-red-800">{r.status}</Badge>
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => !open && setEditDialog({ open: false, type: null, data: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Buchung bearbeiten</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Name</Label>
              <Input
                value={editForm.name || ""}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                className="mt-2"
                data-testid="edit-name-input"
              />
            </div>
            <div>
              <Label>E-Mail</Label>
              <Input
                type="email"
                value={editForm.email || ""}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                className="mt-2"
                data-testid="edit-email-input"
              />
            </div>
            <div>
              <Label>Telefon</Label>
              <Input
                value={editForm.phone || ""}
                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                className="mt-2"
                data-testid="edit-phone-input"
              />
            </div>
            <div>
              <Label>Zahlungsstatus</Label>
              <Select
                value={editForm.payment_status}
                onValueChange={(value) => setEditForm({ ...editForm, payment_status: value })}
              >
                <SelectTrigger className="mt-2" data-testid="edit-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Ausstehend</SelectItem>
                  <SelectItem value="completed">Bezahlt</SelectItem>
                  <SelectItem value="cancelled">Storniert</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog({ open: false, type: null, data: null })}>
              Abbrechen
            </Button>
            <Button onClick={handleUpdateBooking} className="bg-sky-500 hover:bg-sky-600" data-testid="save-edit-btn">
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel / Refund Dialog */}
      <Dialog open={cancelDialog.open} onOpenChange={(open) => !open && setCancelDialog({ open: false, target: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Buchung stornieren</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
              <p><strong>Eintrag:</strong> {cancelDialog.target?.label}</p>
              {!cancelDialog.target?.hasCapture && (
                <p className="mt-2 text-xs text-amber-800">
                  ⚠️ Keine PayPal-Capture-ID hinterlegt – automatische Rückzahlung nicht möglich. Du kannst nur „Stornieren ohne Rückzahlung" wählen.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-semibold">Modus</Label>
              <div className="space-y-2">
                <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer ${cancelForm.refund ? "border-emerald-300 bg-emerald-50" : "border-slate-200 hover:bg-slate-50"} ${!cancelDialog.target?.hasCapture ? "opacity-50 cursor-not-allowed" : ""}`}>
                  <input
                    type="radio"
                    name="cancel-mode"
                    checked={cancelForm.refund === true}
                    disabled={!cancelDialog.target?.hasCapture}
                    onChange={() => setCancelForm({ ...cancelForm, refund: true })}
                    className="mt-1"
                    data-testid="cancel-mode-refund"
                  />
                  <div>
                    <p className="font-medium text-sm">Stornieren + PayPal-Rückzahlung</p>
                    <p className="text-xs text-slate-600">Voller Betrag (inkl. Servicegebühr) wird erstattet.</p>
                  </div>
                </label>
                <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer ${cancelForm.refund === false ? "border-slate-400 bg-slate-50" : "border-slate-200 hover:bg-slate-50"}`}>
                  <input
                    type="radio"
                    name="cancel-mode"
                    checked={cancelForm.refund === false}
                    onChange={() => setCancelForm({ ...cancelForm, refund: false })}
                    className="mt-1"
                    data-testid="cancel-mode-no-refund"
                  />
                  <div>
                    <p className="font-medium text-sm">Nur stornieren (ohne Rückzahlung)</p>
                    <p className="text-xs text-slate-600">Buchung wird storniert, du kümmerst dich selbst um eine Rückerstattung.</p>
                  </div>
                </label>
              </div>
            </div>

            <div>
              <Label>Grund (optional, an Kunde sichtbar)</Label>
              <Input
                value={cancelForm.reason}
                onChange={(e) => setCancelForm({ ...cancelForm, reason: e.target.value })}
                placeholder="z.B. Wartungsarbeiten, Krankheitsfall…"
                className="mt-2"
                data-testid="cancel-reason-input"
              />
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={cancelForm.notify_customer}
                onChange={(e) => setCancelForm({ ...cancelForm, notify_customer: e.target.checked })}
                data-testid="cancel-notify-customer"
              />
              Storno-Mail an Kunden senden
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialog({ open: false, target: null })} disabled={cancelBusy}>
              Abbrechen
            </Button>
            <Button
              onClick={handleCancel}
              disabled={cancelBusy}
              className={cancelForm.refund ? "bg-emerald-600 hover:bg-emerald-700" : "bg-amber-600 hover:bg-amber-700"}
              data-testid="cancel-confirm-btn"
            >
              {cancelBusy ? "…" : (cancelForm.refund ? "Stornieren + Rückzahlung" : "Nur Stornieren")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment-Link Dialog */}
      <Dialog open={linkDialog.open} onOpenChange={(open) => {
        if (!open) { setLinkDialog({ open: false, type: null }); setLinkResult(null); }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              PayPal-Link senden · {linkDialog.type === "silvester" ? "Silvester" : linkDialog.type === "eisstock" ? "Eisstockschießen" : "Gutschein"}
            </DialogTitle>
            <p className="text-xs text-slate-500 mt-1">Der Kunde erhält per E-Mail einen Zahlungslink (72 h gültig). Nach erfolgreicher Zahlung wird die Buchung automatisch bestätigt + PDF zugestellt.</p>
          </DialogHeader>

          {linkResult ? (
            <div className="py-4 space-y-3" data-testid="link-result">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <p className="text-sm font-semibold text-emerald-900 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" /> Zahlungslink versendet
                </p>
                <p className="text-xs text-emerald-800 mt-1">Gesamtpreis: <strong>{Number(linkResult.total).toFixed(2)} €</strong> (inkl. 2 € Servicegebühr)</p>
              </div>
              <div>
                <Label className="text-xs">Zahlungslink (kannst du auch direkt teilen)</Label>
                <div className="flex gap-2 mt-1">
                  <Input value={linkResult.pay_url} readOnly className="font-mono text-xs" data-testid="link-pay-url" />
                  <Button
                    onClick={() => {
                      navigator.clipboard.writeText(linkResult.pay_url);
                      toast.success("Link kopiert");
                    }}
                    variant="outline" size="sm"
                  >Kopieren</Button>
                </div>
              </div>
              <Button onClick={() => { setLinkDialog({ open: false, type: null }); setLinkResult(null); }}
                      className="w-full">Schließen</Button>
            </div>
          ) : (
            <>
              <div className="py-4 space-y-3 max-h-[70vh] overflow-y-auto">
                {linkDialog.type === "gutschein" ? (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Käufer-Name *</Label>
                        <Input value={linkForm.purchaser_name || ""}
                               onChange={(e) => setLinkForm({ ...linkForm, purchaser_name: e.target.value })}
                               className="mt-1" data-testid="link-purchaser-name" />
                      </div>
                      <div>
                        <Label>Empfänger</Label>
                        <Input value={linkForm.recipient_name || ""}
                               onChange={(e) => setLinkForm({ ...linkForm, recipient_name: e.target.value })}
                               className="mt-1" />
                      </div>
                    </div>
                    <div>
                      <Label>Käufer-E-Mail *</Label>
                      <Input type="email" value={linkForm.purchaser_email || ""}
                             onChange={(e) => setLinkForm({ ...linkForm, purchaser_email: e.target.value })}
                             className="mt-1" data-testid="link-purchaser-email" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Anzahl</Label>
                        <Input type="number" min={1} max={20}
                               value={linkForm.quantity || 1}
                               onChange={(e) => setLinkForm({ ...linkForm, quantity: Number(e.target.value) })}
                               className="mt-1" />
                      </div>
                      <div>
                        <Label>Wert pro Gutschein (€)</Label>
                        <Input type="number" min={1} max={5000}
                               value={linkForm.amount_per_voucher || 25}
                               onChange={(e) => setLinkForm({ ...linkForm, amount_per_voucher: Number(e.target.value) })}
                               className="mt-1" />
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Name *</Label>
                        <Input value={linkForm.name || ""}
                               onChange={(e) => setLinkForm({ ...linkForm, name: e.target.value })}
                               className="mt-1" data-testid="link-name" />
                      </div>
                      <div>
                        <Label>Telefon</Label>
                        <Input value={linkForm.phone || ""}
                               onChange={(e) => setLinkForm({ ...linkForm, phone: e.target.value })}
                               className="mt-1" />
                      </div>
                    </div>
                    <div>
                      <Label>E-Mail *</Label>
                      <Input type="email" value={linkForm.email || ""}
                             onChange={(e) => setLinkForm({ ...linkForm, email: e.target.value })}
                             className="mt-1" data-testid="link-email" />
                    </div>
                  </>
                )}

                {linkDialog.type === "silvester" ? (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Ticket-Typ</Label>
                      <Select value={linkForm.ticket_type}
                              onValueChange={(v) => setLinkForm({ ...linkForm, ticket_type: v })}>
                        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="vip">VIP (49 €)</SelectItem>
                          <SelectItem value="normal">Normal (15 €)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Anzahl Tickets</Label>
                      <Input type="number" min={1} max={20}
                             value={linkForm.ticket_count || 1}
                             onChange={(e) => setLinkForm({ ...linkForm, ticket_count: Number(e.target.value) })}
                             className="mt-1" />
                    </div>
                  </div>
                ) : linkDialog.type === "eisstock" ? (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Datum (YYYY-MM-DD)</Label>
                        <Input value={linkForm.booking_date || ""}
                               onChange={(e) => setLinkForm({ ...linkForm, booking_date: e.target.value })}
                               placeholder="2026-12-15"
                               className="mt-1" />
                      </div>
                      <div>
                        <Label>Zeitslot</Label>
                        <Input value={linkForm.time_slot || ""}
                               onChange={(e) => setLinkForm({ ...linkForm, time_slot: e.target.value })}
                               placeholder="17:30-19:00"
                               className="mt-1" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Personen</Label>
                        <Input type="number" min={1} max={20}
                               value={linkForm.group_size || 4}
                               onChange={(e) => setLinkForm({ ...linkForm, group_size: Number(e.target.value) })}
                               className="mt-1" />
                      </div>
                      <div>
                        <Label>Bahn</Label>
                        <Select value={linkForm.lane?.toString() || "auto"}
                                onValueChange={(v) => setLinkForm({ ...linkForm, lane: v })}>
                          <SelectTrigger className="mt-1"><SelectValue placeholder="Auto" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="auto">Automatisch nächste freie</SelectItem>
                            <SelectItem value="1">Bahn 1</SelectItem>
                            <SelectItem value="2">Bahn 2</SelectItem>
                            <SelectItem value="3">Bahn 3</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </>
                ) : null}

                <div>
                  <Label>Notiz im E-Mail-Text (optional)</Label>
                  <Input value={linkForm.note || ""}
                         onChange={(e) => setLinkForm({ ...linkForm, note: e.target.value })}
                         className="mt-1"
                         placeholder="z.B. 'Wie telefonisch besprochen…'" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setLinkDialog({ open: false, type: null })} disabled={linkBusy}>
                  Abbrechen
                </Button>
                <Button
                  onClick={handleSendPaymentLink}
                  disabled={linkBusy}
                  className="bg-sky-600 hover:bg-sky-700"
                  data-testid="link-send-btn"
                >
                  {linkBusy ? "…" : "Zahlungslink versenden"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Manual Booking Dialog */}
      <Dialog open={manualDialog.open} onOpenChange={(open) => !open && setManualDialog({ open: false, type: null })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              Manuelle {manualDialog.type === "silvester" ? "Silvester-" : manualDialog.type === "gutschein" ? "Gutschein-" : "Eisstock-"}Buchung
            </DialogTitle>
            <p className="text-xs text-slate-500 mt-1">Keine 2 € Servicegebühr · Status sofort „bezahlt"</p>
          </DialogHeader>
          <div className="py-4 space-y-3 max-h-[70vh] overflow-y-auto">
            {manualDialog.type === "gutschein" ? (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Käufer-Name *</Label>
                    <Input
                      value={manualForm.purchaser_name || ""}
                      onChange={(e) => setManualForm({ ...manualForm, purchaser_name: e.target.value })}
                      className="mt-1"
                      data-testid="manual-purchaser-name"
                    />
                  </div>
                  <div>
                    <Label>Empfänger (optional)</Label>
                    <Input
                      value={manualForm.recipient_name || ""}
                      onChange={(e) => setManualForm({ ...manualForm, recipient_name: e.target.value })}
                      className="mt-1"
                      placeholder="z.B. Oma Inge"
                    />
                  </div>
                </div>
                <div>
                  <Label>Käufer-E-Mail (optional)</Label>
                  <Input
                    type="email"
                    value={manualForm.purchaser_email || ""}
                    onChange={(e) => setManualForm({ ...manualForm, purchaser_email: e.target.value })}
                    className="mt-1"
                    placeholder="leer lassen wenn nur an dich"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Anzahl Gutscheine</Label>
                    <Input
                      type="number" min={1} max={20}
                      value={manualForm.quantity || 1}
                      onChange={(e) => setManualForm({ ...manualForm, quantity: Number(e.target.value) })}
                      className="mt-1"
                      data-testid="manual-gutschein-quantity"
                    />
                  </div>
                  <div>
                    <Label>Wert pro Gutschein (€)</Label>
                    <Input
                      type="number" min={0} max={5000} step={1}
                      value={manualForm.amount_per_voucher ?? 25}
                      onChange={(e) => setManualForm({ ...manualForm, amount_per_voucher: Number(e.target.value) })}
                      className="mt-1"
                      data-testid="manual-gutschein-amount"
                    />
                    <p className="text-xs text-slate-500 mt-1">0 € bis 5.000 € pro Gutschein (0 € nur in Kombination mit „Kostenlos/Freiplatz")</p>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Name *</Label>
                    <Input
                      value={manualForm.name || ""}
                      onChange={(e) => setManualForm({ ...manualForm, name: e.target.value })}
                      className="mt-1"
                      data-testid="manual-name-input"
                    />
                  </div>
                  <div>
                    <Label>Telefon</Label>
                    <Input
                      value={manualForm.phone || ""}
                      onChange={(e) => setManualForm({ ...manualForm, phone: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </div>
                <div>
                  <Label>E-Mail (optional)</Label>
                  <Input
                    type="email"
                    value={manualForm.email || ""}
                    onChange={(e) => setManualForm({ ...manualForm, email: e.target.value })}
                    className="mt-1"
                    placeholder="leer lassen wenn nur an dich"
                    data-testid="manual-email-input"
                  />
                </div>
              </>
            )}

            {manualDialog.type === "silvester" ? (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Ticket-Typ</Label>
                  <Select value={manualForm.ticket_type} onValueChange={(v) => setManualForm({ ...manualForm, ticket_type: v })}>
                    <SelectTrigger className="mt-1" data-testid="manual-ticket-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="vip">VIP (49 €)</SelectItem>
                      <SelectItem value="normal">Normal (15 €)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Anzahl Tickets</Label>
                  <Input
                    type="number" min={1} max={20}
                    value={manualForm.ticket_count || 1}
                    onChange={(e) => setManualForm({ ...manualForm, ticket_count: Number(e.target.value) })}
                    className="mt-1"
                    data-testid="manual-ticket-count"
                  />
                </div>
              </div>
            ) : manualDialog.type === "eisstock" ? (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Datum (TT.MM.JJJJ)</Label>
                    <Input
                      value={manualForm.booking_date || ""}
                      onChange={(e) => setManualForm({ ...manualForm, booking_date: e.target.value })}
                      placeholder="15.12.2026"
                      className="mt-1"
                      data-testid="manual-booking-date"
                    />
                  </div>
                  <div>
                    <Label>Zeitslot</Label>
                    <Input
                      value={manualForm.time_slot || ""}
                      onChange={(e) => setManualForm({ ...manualForm, time_slot: e.target.value })}
                      placeholder="17:30-19:00"
                      className="mt-1"
                      data-testid="manual-time-slot"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Personen</Label>
                    <Input
                      type="number" min={1} max={20}
                      value={manualForm.group_size || 4}
                      onChange={(e) => setManualForm({ ...manualForm, group_size: Number(e.target.value) })}
                      className="mt-1"
                      data-testid="manual-group-size"
                    />
                  </div>
                  <div>
                    <Label>Bahn</Label>
                    <Select value={manualForm.lane?.toString() || "auto"} onValueChange={(v) => setManualForm({ ...manualForm, lane: v === "auto" ? "" : v })}>
                      <SelectTrigger className="mt-1" data-testid="manual-lane">
                        <SelectValue placeholder="Auto" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="auto">Automatisch nächste freie</SelectItem>
                        <SelectItem value="1">Bahn 1</SelectItem>
                        <SelectItem value="2">Bahn 2</SelectItem>
                        <SelectItem value="3">Bahn 3</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </>
            ) : null}

            <div>
              <Label>Zahlungsart</Label>
              <Select value={manualForm.payment_mode} onValueChange={(v) => setManualForm({ ...manualForm, payment_mode: v })}>
                <SelectTrigger className="mt-1" data-testid="manual-payment-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Bar/Überweisung (vor Ort)</SelectItem>
                  <SelectItem value="paypal">PayPal (manuell vermerkt)</SelectItem>
                  <SelectItem value="free">Kostenlos / Freiplatz (0 €)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">Servicegebühr entfällt bei Admin-Buchungen.</p>
            </div>

            <div className="space-y-2 pt-2 border-t">
              <p className="text-sm font-semibold">Bestätigungsmail senden an:</p>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={!!manualForm.notify_admin}
                  onChange={(e) => setManualForm({ ...manualForm, notify_admin: e.target.checked })}
                  data-testid="manual-notify-admin"
                />
                An mich (info@groemitzereiszeit.de) zum Weiterleiten
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={!!manualForm.notify_customer}
                  onChange={(e) => setManualForm({ ...manualForm, notify_customer: e.target.checked })}
                  data-testid="manual-notify-customer"
                />
                An den Kunden (nur falls E-Mail eingegeben)
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManualDialog({ open: false, type: null })} disabled={manualBusy}>
              Abbrechen
            </Button>
            <Button
              onClick={handleCreateManual}
              disabled={manualBusy}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="manual-create-btn"
            >
              {manualBusy ? "…" : "Buchung erstellen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Block Slot Dialog */}
      <Dialog open={blockDialog.open} onOpenChange={(open) => !open && setBlockDialog({ open: false, date: null, slot: null, lane: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {blockDialog.lane ? `Bahn ${blockDialog.lane} blockieren` : "Ganzen Zeitslot blockieren"}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600 mb-4">
              {blockDialog.lane ? (
                <>
                  Möchtest du <strong>Bahn {blockDialog.lane}</strong> im Zeitslot{" "}
                  <strong>{blockDialog.slot}</strong> am{" "}
                  <strong>{blockDialog.date && format(blockDialog.date, "d. MMMM yyyy", { locale: de })}</strong> blockieren?
                  Die anderen Bahnen bleiben buchbar.
                </>
              ) : (
                <>
                  Möchtest du den gesamten Zeitslot <strong>{blockDialog.slot}</strong> am{" "}
                  <strong>{blockDialog.date && format(blockDialog.date, "d. MMMM yyyy", { locale: de })}</strong> blockieren?
                  Alle 3 Bahnen werden gesperrt.
                </>
              )}
            </p>
            <div>
              <Label>Grund (optional)</Label>
              <Input
                value={blockReason}
                onChange={(e) => setBlockReason(e.target.value)}
                placeholder="z.B. Wartung, Privatveranstaltung..."
                className="mt-2"
                data-testid="block-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBlockDialog({ open: false, date: null, slot: null, lane: null })}>
              Abbrechen
            </Button>
            <Button onClick={handleBlockSlot} className="bg-red-500 hover:bg-red-600" data-testid="confirm-block-btn">
              Blockieren
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm Dialog (unified) */}
      <AlertDialog
        open={deleteDialog.open}
        onOpenChange={(open) => !open && setDeleteDialog({ open: false, item: null })}
      >
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Wirklich löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteDialog.item?.label
                ? `„${deleteDialog.item.label}" wird endgültig gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.`
                : "Dieser Eintrag wird endgültig gelöscht. Diese Aktion kann nicht rückgängig gemacht werden."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-500 hover:bg-red-600"
              data-testid="delete-confirm-btn"
            >
              Ja, löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminDashboard;
